<template>
  <div
    class="
      layout/flex:column
      layout/grid:gap:page:2
    "
    >
    <CollectionForm
      :isCollapsible="isCollapsible"
      :classifications="params.classifications"
      :date-begin="params.date_begin"
      :date-end="params.date_end"
      :include-uncatalogued-works="params.include_uncataloged_works"
      :on-view="params.on_view"
      :q="params.q"
      :recent-acquisitions="params.recent_acquisitions"
      :with-images="params.with_images"
      :classification-filter-options="classificationFilterOptions"
      @form-submit="onFormSubmit"
      @search-input="onSearchInput"
      @search-reset="onSearchReset"
      @search-submit="onSearchSubmit"
      @update-checked="onUpdateChecked"
      @update-classifications="onUpdateClassifications"
      @update-from-year-date="onUpdateFromYearDate"
      @update-to-year-date="onUpdateToYearDate"
    />

    <FadeTransition>
      <div
        class="
          layout/flex:column
          layout/grid:gap:page:3
        "
        >
        <ArtistsSearchList
          v-if="!showSpinner && artistsList.totalCount > 0 && params.q !== ''"
          :artists="artistsList.artists"
          :total-count="artistsList.totalCount"
          :search-term="params.q"
          class="
            $typography/size:large
            layout/align:start
            layout/flex:column
            layout/wrapper
          "
        />
        <div
          class="
            $typography/size:medium
            layout/height:cap
            layout/width:100%
            layout/wrapper
            "
            v-if="!showSpinner"
          >
          <FilteredPaginationFilterCount
            :filtered-total="filteredTotal"
            :label-online="i18n.t('filter-form__label__online')"
            :message-filtered-count="i18n.t('filter-form__filter_count__filtered-count', { FilteredCount: filteredTotal.toLocaleString() })"
            :message-filtered-no-count="i18n.t('filter-form__filter_count__filtered-no-count')"
            :message-search-term-no-results="i18n.t('filter-form__filter_count__no-results-for-search-term', { SearchVal: params.q })"
            :message-total-count-online="i18n.t('filter-form__filter_count__total-count-online', { TotalCount: total.toLocaleString(), Resource: tResource })"
            :message-total-count="i18n.t('filter-form__filter_count__total-count', { TotalCount: total.toLocaleString(), Resource: tResource })"
            :search-val="params.q"
            :total="total"
            data-gtm="collection form / reset form"
            href="/collection/works/"
            @resetForm="onResetForm"
          />
        </div>
        <Spinner v-if="showSpinner" />
      </div>
    </FadeTransition>
  </div>
</template>

<script>
  /**
   * FormWrapper manages the submission of the CollectionForm.
   *
   * The responsibility of this component is to manage state for:
   *  - load filtered results
   *  - load paginated results
   *  - updating browser history
   */
   import i18n from 'Utils/i18n.js';

  // Form
  import ArtistsSearchList from './artists-search-list.vue';
  import CollectionForm from "./collection-form.vue";
  import FadeTransition from 'Components/shared/fade-transition.vue';
  import FilteredPaginationFilterCount from 'Components/filtered-pagination/filter-count.vue';
  import Spinner from 'Components/shared/spinner.vue';
  import { options } from './options';

  // Filter/pagination
  import FilteredPaginationMixin from 'Components/filtered-pagination/filtered-pagination-mixin.js';
  import HistoryStackableMixin from 'Components/filtered-pagination/history-stackable-mixin.js';

  export default {
    components: {
      ArtistsSearchList,
      CollectionForm,
      FadeTransition,
      FilteredPaginationFilterCount,
      Spinner,
    },
    mixins: [
      FilteredPaginationMixin,
      HistoryStackableMixin
    ],
    props: {
      // Form
      dataFilteredTotal: { type: Number, required: true },
      dataTotal: { type: Number, required: true },
      isCollapsible: { type: Boolean, required: true },
      paramClassification: { type: String, required: true },
      paramDateBegin: { type: String, required: true },
      paramDateEnd: { type: String, required: true },
      paramIncludeUncataloguedWorks: { type: Boolean, required: true },
      paramOnView: { type: Boolean, required: true },
      paramRecentAcquisitions: { type: Boolean, required: true },
      paramSearchTerm: { type: String, required: true },
      paramWithImages: { type: Boolean, required: true },
    },

    data() {
      return {
        i18n: new i18n(),
        tResource: 'artworks',

        // Form
        params: {
          classifications: this.paramClassification,
          date_begin: this.paramDateBegin,
          date_end: this.paramDateEnd,
          include_uncataloged_works: this.paramIncludeUncataloguedWorks,
          on_view: this.paramOnView,
          q: this.paramSearchTerm,
          recent_acquisitions: this.paramRecentAcquisitions,
          with_images: this.paramWithImages,
        },
        filteredTotal: this.dataFilteredTotal,
        total: this.dataTotal,

        artistsList: {
          artists: [],
          totalCount: 0,
        },
        classificationFilterOptions: [],
      };
    },

    async mounted() {
      // Handles navigation with back button.
      window.addEventListener("popstate", this.onHandlePopState, false);

      if (this.lastPage > 1) {
        const url = new URL(this.dataFetchBaseURL, window.location);
        const urlSearchParams = this.setUrlSearchParams(this.dataFetchBaseURL, this.params).searchParams;
        this.addPagination(url, urlSearchParams);
      }

      const classifications = await this.fetchClassifications();
      const optionClassificationAny = options.formatOptionClassificationAny(this.filteredTotal);
      this.classificationFilterOptions = [optionClassificationAny, ...classifications];

      if (this.params.q !== '') {
        const artists = await this.fetchArtistsBySearchTerm();
        this.artistsList = {
          artists: artists.results,
          totalCount: artists.total_count,
        };
      }
    },

    beforeDestroy() {
      window.removeEventListener("popstate", this.onHandlePopState);
    },

    methods: {
      /**
       * Callback action emitted from Form child component after form submission.
       */
       onFormSubmit() {
        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from browser when history stack navigation is used (Back/Forwards buttons).
       */
       onHandlePopState() {
        const poppedUrl = new URL(window.location);
        const searchParams = this.extractSearchParamsFromUrl(poppedUrl.searchParams);
        this.params = {...this.params, ...searchParams};

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url);
      },

      /**
       * Callback action emitted from Form child component to reset reactive data.
       */
       onResetForm() {
        this.params = Object.assign(this.params, options.defaultParams);
        this.artistsList = { artists: [], totalCount: 0 };

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from child component to update reactive data related to checkboxes.
       *
       * @param {Object} obj
       * @param {string} obj.name - The name of the param the checkbox is associated with
       * @param {string} obj.value - The boolean value of the checkbox
       */
       onUpdateChecked({name, value}) {
        if (!(name in this.params)) return;
        if (name === '') return;

        this.params[name] = value;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to update reactive `q` data.
       *
       * @param {string} val
       */
       onSearchInput(val) {
        this.params.q = val;
      },

      /**
       * Callback action emitted from Form child component to clear reactive `q` data.
       */
       onSearchReset() {
        this.params.q = '';
        this.artistsList = { artists: [], totalCount: 0 };

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to submit event.
       */
       onSearchSubmit() {
        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to updated reactive `date_begin` data.
       */
       onUpdateFromYearDate(val) {
        this.params.date_begin = val;

        /**
         * Ensure contraint that `end_date` is never < `begin_date`.
         *
         * TODO: Consider updating "Pre-1850s" option to be numeric so that `isNaN()` is not needed.
         */
        const parsedDateBegin = parseInt(this.params.date_begin);
        if (!Number.isNaN(parsedDateBegin) && this.params.date_begin > this.params.date_end) {
          this.params.date_end = this.params.date_begin;
        }

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to updated reactive `date_end` data.
       */
       onUpdateToYearDate(val) {
        this.params.date_end = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Callback action emitted from Form child component to updated reactive `classifications` data.
       */
       onUpdateClassifications(val) {
        this.params.classifications = val;

        const url = this.setUrlSearchParams(this.dataFetchBaseURL, this.params);
        this.fetchAndDisplayResults(url).then(this.addToHistoryStack(url));
      },

      /**
       * Extracts a predefined set of search params from a URL.
       *
       * @param {URLSearchParam} urlSearchParam
       * @returns {Object} An object composed of values extracted from search parameters or default.
       */
       extractSearchParamsFromUrl(urlSearchParam) {
        return {
          classifications: parseInt(urlSearchParam.get('classifications')) || options.defaultParams.classifications,
          date_begin: urlSearchParam.get('date_begin') || options.defaultParams.date_begin,
          date_end: urlSearchParam.get('date_end') || options.defaultParams.date_end,
          include_uncataloged_works: urlSearchParam.get('include_uncataloged_works') || options.defaultParams.include_uncataloged_works,
          on_view: urlSearchParam.get('on_view') || options.defaultParams.on_view,
          q: urlSearchParam.get('q') || options.defaultParams.q,
          recent_acquisitions: urlSearchParam.get('recent_acquisitions') || options.defaultParams.recent_acquisitions,
          with_images: urlSearchParam.get('with_images') || options.defaultParams.with_images,
        };
      },

      /**
       * Wrapper for `fetchData()` to handle shared rendering callback logic.
       *
       * @param {string} url
       */
       async fetchAndDisplayResults(url) {
        this.displaySpinner();
        this.hidePaginationContainer();

        try {
          const results = await this.fetchData(url);
          this.populatePaginationResults(results);

          // TODO: this.filteredTotal is updated via the previous request. Make it more explicit where this change is coming from.
          const classifications = await this.fetchClassifications(url.search);
          const optionClassificationAny = options.formatOptionClassificationAny(this.filteredTotal);
          this.classificationFilterOptions = [optionClassificationAny, ...classifications];

          if (this.params.q !== '') {
            const artists = await this.fetchArtistsBySearchTerm(url.search);
            this.artistsList = {
              artists: artists.results,
              totalCount: artists.total_count,
            };
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        } finally {
          this.hideSpinner();
          this.displayPaginationContainer();

          if (this.lastPage > 1) {
            const urlSearchParams = this.setUrlSearchParams(this.dataFetchBaseURL, this.params).searchParams;
            this.addPagination(url, urlSearchParams);
          } else {
            this.removePagination();
          }
        }
      },

      async fetchArtistsBySearchTerm(searchParams = '') {
        const res = await fetch(`/collection/artists-search.json${searchParams}`);
        if (!res.ok) {
          throw new Error('Failed to fetch artist search data');
        }

        return res.json();
      },

      async fetchClassifications(searchParams = '') {
        const res = await fetch(`/collection/classifications.json${searchParams}`);
        if (!res.ok) {
          throw new Error('Failed to fetch classification data');
        }

        return res.json();
      }
    }
  };
</script>
